import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Button,
  Chip,
  MenuItem,
  Paper,
  Skeleton,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { fetchLoadTests } from '../services/api';
import { useUnits } from '../load_test_components/units';
import { TEST_STATUS } from '../load_test_components/steps';
import AssignJobDialog from '../load_test_components/AssignJobDialog';
import { historyTestPath } from '../load_test_components/testRecord';

// Operations > Load Testing > Historical Data: every recorded test, newest
// first, with filters for job, location, test box, logger and date. The list
// is small, so it is fetched once and filtered here. Each test's job, point
// and test number can be set by hand (most loggers leave them blank). A row
// opens the test: its revisions, and its dashboard with any of them.

const ALL = '';
const NO_BOX = 'none';

// Start and end times are the logger's own clock, sent without a time zone,
// so they are shown as written.
function parseLocal(iso) {
  const d = iso ? new Date(iso) : null;
  return d && !Number.isNaN(d.getTime()) ? d : null;
}

function formatDate(d) {
  return d ? d.toLocaleDateString([], { year: 'numeric', month: 'short', day: 'numeric' }) : '—';
}

function formatTime(d) {
  return d ? d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', hour12: false }) : '';
}

function formatDuration(minutes) {
  if (minutes === null || minutes === undefined) return '—';
  const h = Math.floor(minutes / 60);
  const m = minutes % 60;
  return h ? `${h}h ${String(m).padStart(2, '0')}m` : `${m}m`;
}

// "2026-10-14" for comparing against a date input's value.
function dayKey(d) {
  if (!d) return '';
  const pad = (n) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
}

// Unique {value, label} options from the tests, sorted by label.
function optionsFrom(tests, value, label) {
  const seen = new Map();
  tests.forEach((t) => {
    const v = value(t);
    if (v !== null && v !== undefined && !seen.has(v)) seen.set(v, label(t));
  });
  return [...seen.entries()].map(([v, l]) => ({ value: String(v), label: l }))
    .sort((a, b) => a.label.localeCompare(b.label, undefined, { numeric: true }));
}

function FilterSelect({ label, value, onChange, options, extra }) {
  return (
    <TextField
      select
      size='small'
      label={label}
      value={value}
      onChange={(e) => onChange(e.target.value)}
      SelectProps={{ displayEmpty: true }}
      InputLabelProps={{ shrink: true }}
      sx={{ minWidth: 150, flex: '1 1 150px' }}
    >
      <MenuItem value={ALL}>All</MenuItem>
      {extra}
      {options.map((o) => <MenuItem key={o.value} value={o.value}>{o.label}</MenuItem>)}
    </TextField>
  );
}

function JobCell({ test, onEdit }) {
  const details = [
    test.project_number || test.job_number,
    test.location,
    test.point_number,
    test.test_number != null ? `test ${test.test_number}` : null,
  ].filter(Boolean).join(' · ');
  const hasJob = Boolean(test.project_name || test.job_number);
  // The button sits right after the job's name (or "No job set").
  return (
    <Box sx={{ minWidth: 0 }}>
      <Stack direction='row' alignItems='center' spacing={1}>
        <Typography variant='body2' color={hasJob ? 'text.primary' : 'text.secondary'} sx={{ minWidth: 0 }}>
          {hasJob ? (test.project_name || `Job ${test.job_number}`) : 'No job set'}
        </Typography>
        <Button
          size='small'
          onClick={(e) => { e.stopPropagation(); onEdit(); }}
          sx={{ textTransform: 'none', flexShrink: 0, minWidth: 0, py: 0, px: 0.75 }}
        >
          {hasJob ? 'Edit job' : 'Assign job'}
        </Button>
      </Stack>
      {details && <Typography variant='caption' color='text.secondary' component='div'>{details}</Typography>}
    </Box>
  );
}

function LoadTestHistoryPage() {
  const navigate = useNavigate();
  const units = useUnits();
  const [tests, setTests] = useState(null);
  const [error, setError] = useState(null);
  // The test whose job is being set, or null.
  const [editing, setEditing] = useState(null);
  const jobSaved = (row) => {
    setTests((prev) => prev.map((t) => (t.id === row.id ? row : t)));
    setEditing(null);
  };

  const [job, setJob] = useState('');
  const [location, setLocation] = useState(ALL);
  const [box, setBox] = useState(ALL);
  const [logger, setLogger] = useState(ALL);
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');

  useEffect(() => {
    let current = true;
    fetchLoadTests()
      .then((data) => { if (current) setTests(data.tests || []); })
      .catch((err) => { if (current) setError(err.message); });
    return () => { current = false; };
  }, []);

  const all = tests || [];
  const locations = useMemo(() => optionsFrom(all, (t) => t.location, (t) => t.location), [tests]);
  const boxes = useMemo(() => optionsFrom(all, (t) => t.box_id, (t) => t.box_name || `Box ${t.box_id}`), [tests]);
  const loggers = useMemo(() => optionsFrom(all, (t) => t.logger_id, (t) => t.logger_name), [tests]);

  const shown = useMemo(() => {
    const words = job.trim().toLowerCase().split(/\s+/).filter(Boolean);
    return all.filter((t) => {
      if (words.length) {
        const text = [t.project_name, t.job_number, t.project_number, t.point_number].filter(Boolean).join(' ').toLowerCase();
        if (!words.every((w) => text.includes(w))) return false;
      }
      if (location !== ALL && t.location !== location) return false;
      if (box === NO_BOX && t.box_id != null) return false;
      if (box !== ALL && box !== NO_BOX && String(t.box_id) !== box) return false;
      if (logger !== ALL && String(t.logger_id) !== logger) return false;
      const day = dayKey(parseLocal(t.start_time));
      if (from && day < from) return false;
      if (to && day > to) return false;
      return true;
    });
  }, [tests, job, location, box, logger, from, to]);

  const filtered = Boolean(job.trim() || location || box || logger || from || to);
  const clearFilters = () => {
    setJob(''); setLocation(ALL); setBox(ALL); setLogger(ALL); setFrom(''); setTo('');
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
      <Box>
        <Button startIcon={<ArrowBackIcon />} onClick={() => navigate('/operations/load-test')} sx={{ textTransform: 'none' }}>
          Load Testing
        </Button>
      </Box>

      <Stack direction='row' alignItems='baseline' spacing={1.5}>
        <Typography variant='h5' component='h1' fontWeight={600}>Historical Data</Typography>
        {tests && (
          <Typography color='text.secondary'>
            {filtered ? `${shown.length} of ${all.length} tests` : `${all.length} tests`}
          </Typography>
        )}
      </Stack>

      <Paper sx={{ p: 2, borderRadius: 1, display: 'flex', flexWrap: 'wrap', gap: 1.5, alignItems: 'center' }}>
        <TextField
          size='small'
          label='Job'
          placeholder='Name, job or point number'
          value={job}
          onChange={(e) => setJob(e.target.value)}
          sx={{ flex: '2 1 220px' }}
        />
        <FilterSelect label='Location' value={location} onChange={setLocation} options={locations} />
        <FilterSelect
          label='Test box'
          value={box}
          onChange={setBox}
          options={boxes}
          extra={all.some((t) => t.box_id == null) ? <MenuItem value={NO_BOX}>No box</MenuItem> : null}
        />
        <FilterSelect label='Logger' value={logger} onChange={setLogger} options={loggers} />
        <TextField size='small' type='date' label='From' value={from} onChange={(e) => setFrom(e.target.value)} InputLabelProps={{ shrink: true }} sx={{ flex: '1 1 150px' }} />
        <TextField size='small' type='date' label='To' value={to} onChange={(e) => setTo(e.target.value)} InputLabelProps={{ shrink: true }} sx={{ flex: '1 1 150px' }} />
        {filtered && (
          <Button onClick={clearFilters} sx={{ textTransform: 'none' }}>Clear filters</Button>
        )}
      </Paper>

      <Paper sx={{ borderRadius: 1, overflow: 'hidden' }}>
        {error ? (
          <Typography color='error' sx={{ p: 3 }}>{error}</Typography>
        ) : (
          <TableContainer>
            <Table size='small' sx={{ minWidth: 820 }}>
              <TableHead>
                <TableRow>
                  <TableCell>Date</TableCell>
                  <TableCell>Job</TableCell>
                  <TableCell>Test box · logger</TableCell>
                  <TableCell align='right'>Peak load</TableCell>
                  <TableCell align='right'>Length</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell align='center'>Rev</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {!tests && [0, 1, 2, 3, 4].map((i) => (
                  <TableRow key={i}>
                    <TableCell colSpan={7}><Skeleton animation='wave' height={36} /></TableCell>
                  </TableRow>
                ))}
                {tests && shown.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={7} sx={{ py: 4, textAlign: 'center', color: 'text.secondary' }}>
                      {all.length ? 'No tests match these filters.' : 'No tests have been recorded yet.'}
                    </TableCell>
                  </TableRow>
                )}
                {shown.map((t) => {
                  const start = parseLocal(t.start_time);
                  const status = TEST_STATUS[t.status];
                  return (
                    <TableRow key={t.id} hover onClick={() => navigate(historyTestPath(t.id))} sx={{ cursor: 'pointer' }}>
                      <TableCell sx={{ whiteSpace: 'nowrap' }}>
                        <Typography variant='body2'>{formatDate(start)}</Typography>
                        <Typography variant='caption' color='text.secondary'>{formatTime(start)}</Typography>
                      </TableCell>
                      <TableCell><JobCell test={t} onEdit={() => setEditing(t)} /></TableCell>
                      <TableCell>
                        <Typography variant='body2'>{t.box_name || '—'}</Typography>
                        <Typography variant='caption' color='text.secondary'>{t.logger_name}</Typography>
                      </TableCell>
                      <TableCell align='right' sx={{ whiteSpace: 'nowrap' }}>
                        {t.peak_kips == null ? '—' : `${units.fmtForce(t.peak_kips)} ${units.forceUnit}`}
                      </TableCell>
                      <TableCell align='right' sx={{ whiteSpace: 'nowrap' }}>{formatDuration(t.duration_min)}</TableCell>
                      <TableCell>
                        {status
                          ? (
                            <Tooltip arrow title={status.description || ''}>
                              <Chip size='small' variant='outlined' color={status.color} label={status.label} />
                            </Tooltip>
                          )
                          : <Typography variant='body2' color='text.secondary'>—</Typography>}
                      </TableCell>
                      <TableCell align='center'>{t.rev_number ?? '—'}</TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Paper>

      <AssignJobDialog open={Boolean(editing)} test={editing} onClose={() => setEditing(null)} onSaved={jobSaved} />
    </Box>
  );
}

export default LoadTestHistoryPage;
